import { useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  CLIQUE_SCOPES,
  DefaultIdentityIssuerMode,
  InvitationApproveInUnit,
  type CliqueThemeConfig,
} from '@authifi/clique-react-ui';
import { CliqueAuthProvider } from '@authifi/clique-react-ui/auth';
import {
  AUTHORITY,
  REDIRECT_URI,
  API_BASE_URL,
  CLIENT_ID,
  DEFAULT_TENANT_ID,
  SESSION_IDLE_TIMEOUT_MS,
  TOKEN_NEAR_EXPIRY_WINDOW_MS,
  ENABLE_SESSION_DIAGNOSTICS,
} from './auth-config';
import { DEMO_NAMESPACE_ID, DemoNamespaceFilterProvider } from './demo-namespace-filter';
import {
  DEMO_RESOURCE_SERVER_IDENTIFIER,
  DemoPermissionScopeProvider,
} from './demo-permission-scope';
import { router } from './routes';
import { SignInPage } from './pages/SignInPage';

// Demonstrates the library's appearance customization: brand design tokens
// (light + dark), and branding (app name / logo) consumed by the shell chrome.
const cliqueTheme: CliqueThemeConfig = {
  cssLayer: 'components',
  // Persist this app's theme under its own key so other Clique apps on the same
  // origin don't clobber the selection.
  storageKey: 'clique-shell-theme',
  branding: {
    appName: 'Clique',
    logo: <i aria-hidden="true" className="pi pi-bolt text-xl" />,
    supportUrl: 'https://github.com/authifi/clique',
    footer: <span>© {new Date().getFullYear()} Clique</span>,
  },
  // Locale-aware date formatting used by list columns and detail views.
  format: {
    locale: 'en-US',
    dateOptions: { dateStyle: 'medium' },
  },
  tokens: {
    gradientTo: '#0ea5e9',
  },
  darkTokens: {
    sidebarBg: '#0b1220',
  },
  // We intentionally do NOT override `primaryColor` here so the primary color
  // follows the active preset theme — switching presets (or the theme selector)
  // updates it, and the Playground's brand-token inputs default to the preset's
  // real color. The override capability still exists: setting `primaryColor`
  // recolors even the styled-mode Lara preset, because CliqueThemeProvider
  // tokenizes the preset's baked primary palette into CSS variables
  // (see ThemeProvider.tsx → tokenizePrimaryPalette / primaryPaletteToCss).
  primeTokens: {
    borderRadius: '8px',
  },
};

export function App() {
  const [namespaceFilterEnabled, setNamespaceFilterEnabled] = useState(false);
  const [permissionScopeEnabled, setPermissionScopeEnabled] = useState(true);

  return (
    <DemoNamespaceFilterProvider
      value={{
        enabled: namespaceFilterEnabled,
        setEnabled: setNamespaceFilterEnabled,
        namespaceId: DEMO_NAMESPACE_ID,
      }}
    >
      <DemoPermissionScopeProvider
        value={{
          enabled: permissionScopeEnabled,
          setEnabled: setPermissionScopeEnabled,
          identifier: DEMO_RESOURCE_SERVER_IDENTIFIER,
        }}
      >
        <CliqueAuthProvider
          authority={AUTHORITY}
          clientId={CLIENT_ID}
          apiBaseUrl={API_BASE_URL}
          redirectUri={REDIRECT_URI}
          tenantId={DEFAULT_TENANT_ID}
          scopes={CLIQUE_SCOPES.all}
          resourceServer={{
            identifier: DEMO_RESOURCE_SERVER_IDENTIFIER,
            showSelector: true,
            scopePermissions: permissionScopeEnabled,
          }}
          defaultIdentityIssuer={{ value: 'Azure', mode: DefaultIdentityIssuerMode.Fallback }}
          invitationApprove={{ in: 7, unit: InvitationApproveInUnit.Day }}
          userCreate={{ enabled: true }}
          namespaceId={namespaceFilterEnabled ? DEMO_NAMESPACE_ID : undefined}
          theme={cliqueTheme}
          idleTimeoutMs={SESSION_IDLE_TIMEOUT_MS}
          nearExpiryWindowMs={TOKEN_NEAR_EXPIRY_WINDOW_MS}
          enableSessionDiagnostics={ENABLE_SESSION_DIAGNOSTICS}
          renderSignIn={() => <SignInPage />}
        >
          <RouterProvider router={router} />
          <ReactQueryDevtools initialIsOpen={false} />
        </CliqueAuthProvider>
      </DemoPermissionScopeProvider>
    </DemoNamespaceFilterProvider>
  );
}
